import { useProjectParams } from '@hooks/useProjectParams';
import { ProjectMethodology } from 'components/ProjectMethodology';
import { ProjectStage } from 'components/ProjectStage';
import { ProjectStatus } from 'components/ProjectStatus';
import { ReadField } from 'components/ReadField';
import { Toolbar } from 'components/Toolbar';
import { RequestStatus } from 'features/request/requestSlice';
import type { ProjectContractTypeEnum } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { getContractTypeLabel } from 'types/Contract';
import { methodologyMap } from 'types/methodology';
import { Button, Loader, Text } from 'uikit';
import { formatNumberToRu } from 'utils/formatNumberToRu';

import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ProjectCopy } from '../ProjectCopy/ProjectCopy';
import { ProjectDelete } from '../ProjectDelete';

import { ValueOrServiceMessage } from './ValueOrServiceMessage';

export const ProjectRead = () => {
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const { project: projectRules } = useAccess();
    const { id, methodology } = useProjectParams();
    const navigate = useNavigate();

    const { state }: { state: { refreshProject: boolean } } = useLocation();

    const { projectData, projectDataStatus, refreshProject, validationProjectData } = useProjectData(id);

    useEffect(() => {
        if (projectData && projectDataStatus === RequestStatus.SUCCESS) {
            sendGpnAnalyticsEvent({
                componentId: 'rim_project page_onloaded',
                constaeventtype: 'undefined',
                component: 'OTHER',
                projectId: projectData.uniqueId,
                methodology: projectData.methodology,
                projectName: projectData.name,
                projectType: projectData.contractType,
                projectDO: projectData.subsidiary?.name,
                sendOnce: true,
            });
        }
    }, [projectData, projectDataStatus, sendGpnAnalyticsEvent]);

    const isWorkOrderLength = !!projectData?.contractNavigation?.data?.workOrderNavs?.length;
    const isAvailableWorkOrderOrCalculation = projectData?.contractNavigation?.data?.contractNavData?.hasWorkOrders;

    const textTooltipDelete = `Удаление проекта невозможно. В проекте создан ${
        isWorkOrderLength ? 'Наряд-Заказ' : 'Расчет по договору'
    }`;

    const handleEditBtnClick = useCallback(() => {
        navigate(`/projects/${methodology}/${id}/edit`);
    }, [id, methodology, navigate]);

    useEffect(() => {
        if (state?.refreshProject) {
            refreshProject();
        }
    }, [state?.refreshProject, refreshProject]);

    const isBimProject = useMemo(() => methodology === methodologyMap[ProjectMethodologyEnum.Bim], [methodology]);
    const isUerProject = useMemo(
        () =>
            methodology === methodologyMap[ProjectMethodologyEnum.Uer] &&
            projectData?.calculationNavigation?.data?.calculationNavData?.hasCalculation,
        [methodology, projectData?.calculationNavigation?.data?.calculationNavData?.hasCalculation],
    );

    return (
        <>
            {projectData && validationProjectData?.result && (
                <ProjectPageLayout title={projectData.name}>
                    <Toolbar fullLine>
                        <Button
                            label="Редактировать"
                            size="s"
                            onClick={handleEditBtnClick}
                            disabled={!projectRules.canEdit}
                            view="secondary"
                        />
                        {(isBimProject || isUerProject) && <ProjectCopy />}
                        <ProjectDelete
                            projectId={id}
                            isDisabled={isAvailableWorkOrderOrCalculation}
                            statusProject={projectData.status}
                            isOpenTooltipDelete={isAvailableWorkOrderOrCalculation}
                            textTooltipDelete={textTooltipDelete}
                        />
                    </Toolbar>
                    <div style={{ width: 640 }}>
                        <h3 style={{ fontWeight: 400 }}>Основная информация</h3>
                        <ReadField label="Наименование" value={projectData.name} />
                        <ReadField
                            label="Методология расчета"
                            value={projectData.methodology}
                            render={(value) => <ProjectMethodology methodology={value} />}
                        />
                        <ReadField
                            label="Этап"
                            value={projectData.stage}
                            render={(value) => <ProjectStage id={value} />}
                        />
                        <ReadField
                            label="Статус"
                            value={projectData.status}
                            render={(value) => <ProjectStatus id={value} />}
                        />
                        <ReadField label="Код проекта" value={projectData.code} />
                        <ReadField
                            label="Дата изменения"
                            value={projectData.updatedAt}
                            render={(value) => <Text size="s">{new Date(value).toLocaleDateString()}</Text>}
                        />
                        <ReadField
                            label="Дата создания"
                            value={projectData.createdAt}
                            render={(value) => <Text size="s">{new Date(value).toLocaleDateString()}</Text>}
                        />
                        <ReadField label="Автор" value={projectData.authoredBy} />
                        <ReadField label="Дочернее общество" value={projectData.subsidiary?.name} />
                        {isBimProject && (
                            <ReadField
                                label="Тип договора"
                                value={getContractTypeLabel(projectData.contractType as ProjectContractTypeEnum)}
                            />
                        )}
                        <h3 style={{ fontWeight: 400 }}>Цена проекта</h3>
                        {projectData?.contractPrice && (
                            <>
                                <ReadField
                                    label="Цена договора без НДС (руб.)"
                                    value={projectData?.contractPrice}
                                    render={(value) => (
                                        <ValueOrServiceMessage
                                            serviceData={value}
                                            displayValue={formatNumberToRu(value?.data?.contractPlan, 2)}
                                        />
                                    )}
                                />
                                {projectData?.contractPrice?.data?.hasActualCost && (
                                    <ReadField
                                        label="Цена фактическая без НДС (руб.)"
                                        value={projectData?.contractPrice}
                                        render={(value) => (
                                            <ValueOrServiceMessage
                                                serviceData={value}
                                                displayValue={formatNumberToRu(value?.data?.actualCost, 2)}
                                            />
                                        )}
                                    />
                                )}
                            </>
                        )}
                        {projectData?.calculationPrice && (
                            <ReadField
                                label="Цена расчета НМЦ (руб.)"
                                value={projectData?.calculationPrice}
                                render={(value) => (
                                    <ValueOrServiceMessage
                                        serviceData={value}
                                        displayValue={formatNumberToRu(value?.data?.priceNMC, 2)}
                                    />
                                )}
                            />
                        )}
                    </div>
                </ProjectPageLayout>
            )}
            {projectDataStatus === RequestStatus.FETCHING && <Loader />}
        </>
    );
};

ProjectRead.displayName = 'ProjectRead';
